import { formatToLongDate } from "../../components/Shared/utils";
import {
  allColumnsType,
  tableDataType,
} from "../../types/PackageHistory/package-history.types";
import { utils, writeFile } from "xlsx";
import { sortByDate } from "./table-sort-filter";
import { formatDateToISO } from "../../pages/Admin/TransferIds/TransferIdTable/utils";

export const onFilterChange = (
  key: string,
  value: any,
  data: any[],
  filters: Record<string, any>,
  setFilters: (filters: Record<string, any>) => void,
  setFilteredData: (data: any[]) => void,
) => {
  if (key === "search") {
    const looksLikeDate = (v: string) =>
      (/^\d{1,2}-[a-zA-Z]+(-\d{2,4})?$/).test(v.trim()) ||
      (/^[a-zA-Z]+-\d{2,4}$/).test(v.trim());
    const keyword =
      value.includes("-") && looksLikeDate(value)
        ? formatDateToISO(value).toLowerCase().trim()
        : value.toLowerCase().trim();
    const filtered = data.filter((item) =>
      Object.values(item).some((field) =>
        field?.toString().toLowerCase().includes(keyword),
      ),
    );
    setFilteredData(filtered);
  } else {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    const filtered = data.filter((item) =>
      Object.keys(newFilters).every((filterKey) => {
        const typedFilterKey = filterKey as string;
        const filterValue = newFilters[typedFilterKey];
        if (!filterValue) return true;

        if (typedFilterKey === "receipt_date_time" || typedFilterKey === "updated_when") {
          return item[typedFilterKey]?.split(" ")[0] === filterValue;
        }
        if (typedFilterKey === "msd_compound") {
          return item[typedFilterKey]
            ?.split("-")[0]
            .toLowerCase()
            .includes(filterValue.toLowerCase());
        }

        if (typeof filterValue === "string") {
          return item[typedFilterKey]
            ?.toString()
            .toLowerCase()
            .includes(filterValue.toLowerCase());
        }
        return item[typedFilterKey] === filterValue;
      })
    );
    setFilteredData(filtered);
  }
};

export const filterUniqueValues = (
  field: string,
  filteredData: any[],
  isoDateRegex: RegExp,
) => {
  const uniqueValues: Set<string> = new Set();
  filteredData.forEach((item: any) => {
    const value = item[field];
    if (value) {
      if (field === "msd_compound") {
        uniqueValues.add(value.split("-")[0]);
      } else if (isoDateRegex.test(value)) {
        const formattedDate = formatToLongDate(value);
        uniqueValues.add(formattedDate);
      } else {
        uniqueValues.add(value);
      }
    }
  });

  return Array.from(uniqueValues)
    .sort((a, b) => String(a).localeCompare(String(b)))
    .map((value) => ({ text: value, value }));
};

export const sortFilterData = (date: any) => {
  return date
    .sort((a: { text: string, value: string }, b: { text: string, value: string }) => {
      const dateA = new Date(a.value);
      const dateB = new Date(b.value);
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        return dateA.getTime() - dateB.getTime(); // Sort by date if both are valid dates
      }
      return String(a.value).localeCompare(String(b.value)); // Default string comparison
    })
    .map((value: { text: string; value: string }) => ({
      text: value.text,
      value: value.value,
    }));
};

export const generatePDF = (
  allColumns: allColumnsType,
  visibleColumns: string[],
  dataForExport: tableDataType[],
  jsPDF: any,
  autoTable: any,
) => {
  const tableHeader = [
    allColumns
      .filter(({ key }) => visibleColumns.includes(key))
      .map(({ title }) => title),
  ];

  const tableData = dataForExport.map((obj) => {
    return Object.values(obj);
  });

  const colCount = visibleColumns.length;
  const widthPerCol = Math.floor(270 / colCount);

  const tableHeadersStyling: { [key: string]: { cellWidth: number } } = {};
  tableHeader[0].forEach((title, index) => {
    if (title) {
      tableHeadersStyling[index] = { cellWidth: widthPerCol };
    }
  });

  const doc = new jsPDF({ orientation: "landscape" });

  autoTable(doc, {
    head: tableHeader, // array of array column titles
    body: tableData, // array of array of column values
    tableWidth: "auto",
    columnStyles: tableHeadersStyling,
  });

  doc.save(`package_history_${getCurrentDateTimeForFilename()}.pdf`);
};

export const generateExcel = (
  dataForExport: tableDataType[],
  allColumns: allColumnsType,
) => {
  const mappedData = mapKeysToColumnTitles(dataForExport, allColumns);
  const wb = utils.book_new();
  const ws = utils.json_to_sheet(mappedData);
  utils.book_append_sheet(wb, ws, `package-history`);
  writeFile(wb, `package_history_${getCurrentDateTimeForFilename()}.xlsx`);
};

export const mapKeysToColumnTitles = (
  data: any[],
  allColumns: { dataIndex: string; title: string }[],
): any[] => {
  const keyToTitle: Record<string, string> = {};
  allColumns.forEach((col) => {
    keyToTitle[col.dataIndex] = col.title;
  });

  return data.map((row) => {
    const newRow: Record<string, any> = {};
    Object.keys(row).forEach((key) => {
      newRow[keyToTitle[key] || key] = row[key];
    });
    return newRow;
  });
};

export const sortDataOnDate = (data: tableDataType[]) => {
  return data.sort((a, b) => sortByDate(a, b, "updated_when", false));
};

export const getCurrentDateTimeForFilename = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const time =
    String(date.getHours()).padStart(2, "0") +
    "꞉" +
    String(date.getMinutes()).padStart(2, "0") +
    "꞉" +
    String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}-${time}`;
};

export const downloadFileUsingPreSignedURL = (url: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.rel = "noreferrer noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadFileUsingBase64 = (zipBase64: string) => {
  const zipBlob = new Blob([Uint8Array.from(atob(zipBase64), c => c.charCodeAt(0))], { type: "application/zip" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(zipBlob);
  link.download = `transfer package level reports ${getCurrentDateTimeForFilename()}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
